/**
 * Phase 0 boot sequence. The page loads as a black screen with a boot log
 * on the right; once the CPU, GPU, and audio workers each report "ready"
 * we dynamically import the Solid shell and fade the boot overlay out.
 * Keeping the Solid runtime out of the boot-critical path means the black
 * screen appears immediately and the UI chunk only loads when the core
 * has actually come up.
 */

import type {
  AudioToMainMessage,
  CPUToMainMessage,
  GPUToMainMessage,
  MainToAudioMessage,
  MainToCPUMessage,
  MainToGPUMessage,
  SharedMemory,
} from "@bindings/protocol";
import { detectCapabilities, type PlatformCapabilities } from "./capabilities";
import { AudioRing } from "../bindings/audioring";
import {
  audioContextOptions,
  buildSetGain,
  buildSetMuted,
  buildWorkletInit,
  PROCESSOR_NAME,
  rateMatchesCore,
  workletModuleUrl,
} from "../bindings/audiosetup";
import { handleDroppedFile, type DroppableFile, type PostToCPU } from "./drop";
import { INPUT_BYTES, startGamepadPolling } from "./input";
import { startRemoteHost, type RemoteHostSession } from "./remote/host";
import { appendLogLine, setStatus } from "./log";
import {
  type SettingsStorage,
  createLocalStorageSettings,
  createMemorySettingsStorage,
} from "./ui/state/settings";
import {
  type ManifestStore,
  type SaveDataStorage,
  createLocalStorageManifest,
  createMemoryManifestStore,
  createMemorySaveDataStorage,
  createOpfsSaveDataStorage,
} from "./ui/state/savedata";
import {
  SaveOrchestrator,
  prepareLoadGame,
  resolveImport,
  resolveSaveForPersist,
  type RunIdentity,
} from "./ui/state/saveorch";
import {
  type LibraryStorage,
  createLocalStorageLibrary,
  createMemoryLibraryStorage,
} from "./ui/state/library";
import { sniffFormat } from "./formats";

/** Guest memory sizing. 4 GB guest RAM requires memory64 + SharedArrayBuffer;
 * for Phase 0 we only touch a small slice, but the allocation size matches
 * the Switch 1 RAM budget so we catch out-of-memory problems early. */
const GUEST_RAM_BYTES   = 4 * 1024 * 1024 * 1024;
const GUEST_RAM_BYTES_FALLBACK = 16 * 1024 * 1024; // for testing on devices that can't do 4 GB
const FRAME_SYNC_BYTES  = 4;
const AUDIO_RING_BYTES  = 1 * 1024 * 1024;
const TRACE_BUFFER_BYTES = 2 * 1024 * 1024;

const WORKER_READY_TIMEOUT_MS = 10_000;

function fatal(reason: string): void {
  setStatus(`fatal: ${reason}`);
  appendLogLine("error", reason);
}

function reportCapabilities(caps: PlatformCapabilities): void {
  const lines: string[] = [];
  (Object.keys(caps) as Array<keyof PlatformCapabilities>).forEach(key => {
    lines.push(`${key}=${caps[key] ? "yes" : "no"}`);
  });
  appendLogLine("debug", `capabilities: ${lines.join(", ")}`);
}

function allocateSharedMemory(): SharedMemory | null {
  const build = (ramBytes: number): SharedMemory => ({
    guestRAM:  new SharedArrayBuffer(ramBytes),
    frameSync: new SharedArrayBuffer(FRAME_SYNC_BYTES),
    audioRing: new SharedArrayBuffer(AUDIO_RING_BYTES),
    traceBuf:  new SharedArrayBuffer(TRACE_BUFFER_BYTES),
    input:     new SharedArrayBuffer(INPUT_BYTES),
  });
  try {
    return build(GUEST_RAM_BYTES);
  } catch {
    appendLogLine("warn", "4 GiB allocation failed, falling back to 16 MiB");
    try {
      return build(GUEST_RAM_BYTES_FALLBACK);
    } catch (e) {
      appendLogLine("error", `Fallback SharedArrayBuffer allocation failed: ${(e as Error).message}`);
      return null;
    }
  }
}

/** Drag-and-drop a game file onto the page. The format is sniffed up front so
 * encrypted/unsupported files get an immediate, friendly error (pointing at the
 * dumping guide) instead of being shipped to the core. The read/sniff/route
 * wiring lives in handleDroppedFile (drop.ts) so it can be tested headlessly. */
function setupGameDrop(post: PostToCPU): void {
  window.addEventListener("dragover", (e: DragEvent) => e.preventDefault());
  window.addEventListener("drop", (e: DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    void handleDroppedFile(file, post, appendLogLine);
  });
}

/** Optionally auto-load a homebrew named by `?load=<url>` (e.g. ?load=/fbtest.nro),
 * so the display path can be exercised without a manual drag-and-drop. Routes
 * through the same handleDroppedFile wiring as drag-drop (a fetched Blob is a
 * DroppableFile), so the two paths share one read/sniff/route implementation. */
function autoLoadFromQuery(post: PostToCPU): void {
  const url = new URLSearchParams(window.location.search).get("load");
  if (!url) return;
  appendLogLine("info", `auto-loading ${url}`);
  fetch(url)
    .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.arrayBuffer(); })
    .then((buf) => handleDroppedFile(
      { name: url, size: buf.byteLength, arrayBuffer: () => Promise.resolve(buf) },
      post,
      appendLogLine,
    ))
    .catch((err: unknown) => appendLogLine("error", `auto-load failed: ${(err as Error).message}`));
}

function attachWorkerLogRelay<TMessage extends { type: string }>(
  workerName: string,
  worker: Worker,
  readyHandler: (msg: TMessage) => void,
  failHandler: (reason: string) => void,
): void {
  worker.addEventListener("message", (event: MessageEvent<TMessage>) => {
    const msg = event.data;
    if (msg.type === "log") {
      const logMsg = msg as TMessage & { level: "debug" | "info" | "warn" | "error"; message: string };
      appendLogLine(logMsg.level, `${workerName}: ${logMsg.message}`);
      return;
    }
    if (msg.type === "error") {
      const errMsg = msg as TMessage & { message: string };
      appendLogLine("error", `${workerName} error: ${errMsg.message}`);
      failHandler(errMsg.message);
      return;
    }
    readyHandler(msg);
  });
  worker.addEventListener("error", (event: ErrorEvent) => {
    appendLogLine("error", `${workerName} uncaught: ${event.message}`);
    failHandler(event.message || "uncaught worker error");
  });
}

/**
 * Run-lifecycle events the UI's run-state machine (src/ui/state/run.ts) reacts to.
 * These are the CPU worker's lifecycle messages, distilled to what the In-Game
 * screen needs (the framebuffer pixels stay between the workers; the UI only
 * needs to know that a frame happened, that the title loaded, halted, or failed).
 */
export type RunEvent =
  | { readonly type: "loaded";    readonly name: string }
  | { readonly type: "frame" }
  | { readonly type: "halted" }
  | { readonly type: "loadError"; readonly name: string; readonly message: string }
  /** The guest's save VFS came back from the core after a run, mapped to the
   *  title it belongs to. The shell persists `bytes` under `titleId` (the tested
   *  persistSave path) and refreshes the Detail save info. §1.6: opaque save
   *  bytes — the game's own runtime data, never decrypted content. */
  | { readonly type: "save"; readonly titleId: string; readonly bytes: Uint8Array };

interface BootResult {
  readonly adapterLabel: string;
  readonly cpuBackend:   string;
  readonly ramMiB:       number;
  /** Read, sniff, and route a file through the SAME live pipeline as drag-drop
   *  (formats.ts -> CPU worker). The Onboarding screen's file picker uses this so
   *  picking a file and dropping one go through one implementation. Resolves true
   *  if the file was handed to the core. */
  readonly loadFile: (file: DroppableFile) => Promise<boolean>;
  /** Stop a running title (posts the `halt` message the CPU worker honours).
   *  The In-Game pause overlay's "Stop" uses this. Idempotent. */
  readonly haltRun: () => void;
  /** Set how the display canvas fits the window: keep aspect (letterbox) or
   *  stretch to fill. Drives CSS `object-fit` on the real on-screen canvas; the
   *  Settings screen's "Keep aspect ratio" toggle uses this. */
  readonly setDisplayAspect: (keepAspect: boolean) => void;
  /** Set the runtime mute-audio preference; posts `setMuted` to the audio worker.
   *  The Settings screen's "Mute audio" toggle uses this. */
  readonly setAudioMuted: (muted: boolean) => void;
  /** Set the runtime volume preference (settings.audioGain); posts `setGain` to
   *  the audio worker + worklet. The Settings screen's volume slider uses this. */
  readonly setAudioGain: (gain: number) => void;
  /** Real persistence for settings (localStorage), behind the tested seam. */
  readonly settingsStorage: SettingsStorage;
  /** Real persistence for per-title save-data blobs (OPFS), behind the tested
   *  seam. The Detail screen reconciles a manifest from this to show has-save /
   *  size; the actual blob round-trip into the core's `ctx->savefs` lands once
   *  the C export/import hook exists (this is the storage half). */
  readonly saveDataStorage: SaveDataStorage;
  /** Real persistence for the save-data manifest accounting (localStorage),
   *  behind the tested seam. App persists the reconciled manifest here on mount
   *  and after a save change (e.g. clearing a save), so write times survive a
   *  reload. The blobs live behind {@link saveDataStorage}; this is only the JSON
   *  record of them. */
  readonly manifestStore: ManifestStore;
  /** Real persistence for the imported-title library (localStorage), behind the
   *  tested seam. App loads it on mount and saves after each import/removal so the
   *  library survives a reload. Metadata only — never game bytes (§1.6). */
  readonly libraryStore: LibraryStorage;
  /** Subscribe to run-lifecycle events (loaded/frame/halted/loadError) so the
   *  In-Game run-state machine reflects the live core. Returns an unsubscribe. */
  readonly onRunEvent: (cb: (event: RunEvent) => void) => () => void;
  /** Track C: start a phone-as-controller pairing session; the Remote screen
   *  renders the returned session's pairing URL and its input is merged into the
   *  shared buffer by the gamepad loop. */
  readonly startRemoteControl: () => RemoteHostSession;
  /** Tear down the active pairing session. */
  readonly stopRemoteControl: () => void;
}

/**
 * The live audio-output graph, returned by {@link setupAudioOutput}: the worklet
 * node (its `port` carries init + runtime mute) and the resume hook the autoplay
 * policy requires (an AudioContext starts "suspended" until a user gesture). Null
 * is returned when WebAudio or AudioWorklet is unavailable (the rest of the app
 * still boots; audio is just silent) — the consumers null-check it.
 */
interface AudioOutput {
  /** Forward a runtime mute change to the worklet (mirrors the audio worker). */
  readonly setMuted: (muted: boolean) => void;
  /** Forward a runtime volume change to the worklet (settings.audioGain). */
  readonly setGain: (gain: number) => void;
  /** Resume the (autoplay-suspended) context; safe to call repeatedly. */
  readonly resume: () => void;
}

/**
 * Stand up the WebAudio output graph: create an AudioContext at the core's rate,
 * load + register the worklet module, create the worklet node (2-in/2-out), wire
 * it to the destination, and hand it the SAB ring the cpu.worker producer also
 * holds. The context starts suspended (autoplay policy); the caller resumes it on
 * the first user gesture via the returned {@link AudioOutput.resume}.
 *
 * This is the irreducibly-browser glue (the format/init contract it uses lives in
 * the headlessly-tested bindings/audiosetup.ts); actual audible sound is the one
 * manual in-browser check (docs/HANDOFF.md §12). Returns null (and logs) if the
 * environment lacks WebAudio/AudioWorklet, so the app still boots silently.
 */
async function setupAudioOutput(audioRing: SharedArrayBuffer): Promise<AudioOutput | null> {
  const Ctor: typeof AudioContext | undefined =
    typeof AudioContext !== "undefined"
      ? AudioContext
      : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) {
    appendLogLine("warn", "WebAudio unavailable; audio output disabled (app continues, silent)");
    return null;
  }

  let ctx: AudioContext;
  try {
    ctx = new Ctor(audioContextOptions());
  } catch (e) {
    appendLogLine("warn", `AudioContext creation failed: ${(e as Error).message}; audio disabled`);
    return null;
  }

  if (!rateMatchesCore(ctx.sampleRate)) {
    appendLogLine(
      "info",
      `AudioContext rate ${ctx.sampleRate} Hz != core 48000 Hz (browser ignored the 48000 Hz ` +
        "request); the worklet linearly resamples to the context rate (correct pitch/speed)",
    );
  }

  /* The worklet processor module URL: a fixed, transpiled JS chunk in production
   * (the dedicated Rollup entry in vite.config.ts) or the dev-server-transpiled
   * .ts source in dev. `addModule` runs its registerProcessor side effect in the
   * AudioWorkletGlobalScope. */
  const moduleUrl = workletModuleUrl(import.meta.env.DEV, import.meta.env.BASE_URL);
  try {
    await ctx.audioWorklet.addModule(moduleUrl);
  } catch (e) {
    appendLogLine("warn", `audioWorklet.addModule failed: ${(e as Error).message}; audio disabled`);
    void ctx.close();
    return null;
  }

  let node: AudioWorkletNode;
  try {
    node = new AudioWorkletNode(ctx, PROCESSOR_NAME, {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 2,
    });
  } catch (e) {
    appendLogLine("warn", `AudioWorkletNode creation failed: ${(e as Error).message}; audio disabled`);
    void ctx.close();
    return null;
  }

  node.connect(ctx.destination);
  /* Hand the worklet the SAME SAB the cpu.worker produces into, the core channel
   * count, and the boot mute default (false; the App's settings effect pushes the
   * persisted preference right after mount via setMuted). */
  node.port.postMessage(buildWorkletInit(audioRing, false));
  appendLogLine("info", `audio output graph up (worklet "${PROCESSOR_NAME}" @ ${ctx.sampleRate} Hz)`);

  return {
    setMuted: (muted: boolean): void => node.port.postMessage(buildSetMuted(muted)),
    setGain: (gain: number): void => node.port.postMessage(buildSetGain(gain)),
    resume: (): void => { if (ctx.state === "suspended") void ctx.resume(); },
  };
}

async function boot(): Promise<BootResult | null> {
  appendLogLine("info", "Voland web - Phase 0 skeleton booting");

  /* 1. Capability detection. */
  const caps = detectCapabilities();
  reportCapabilities(caps);

  if (!caps.sharedArrayBuffer) {
    fatal("cross-origin isolation inactive, SharedArrayBuffer is unavailable. " +
          "Serve with COOP: same-origin and COEP: require-corp.");
    return null;
  }
  if (!caps.webGPU) {
    appendLogLine("warn", "WebGPU unavailable. GPU worker will not produce pixels, but the boot flow will still validate.");
  }

  /* 2. Shared memory. */
  const shared = allocateSharedMemory();
  if (!shared) {
    fatal("failed to allocate guest RAM (4 GiB SharedArrayBuffer). Close tabs or lower the size.");
    return null;
  }
  const ramMiB = Math.round(shared.guestRAM.byteLength / (1024 * 1024));
  appendLogLine("info", `allocated guest RAM: ${ramMiB} MiB`);

  /* Reset the audio transport ring ONCE here, on the owning main thread, before
   * either the cpu.worker producer or the AudioWorklet consumer constructs a view
   * over the same SharedArrayBuffer (both do construct-only, no reset). This zeroes
   * the read/write indices so the ring starts empty. */
  new AudioRing(shared.audioRing).reset();

  /* Gamepad polling publishes input into the shared buffer for the CPU worker.
   * A phone controller (Track C), when paired, is folded into the SAME buffer in
   * the SAME rAF right after the local snapshot — deterministic OR-merge order,
   * no second loop racing the poller. The session is created lazily by the Remote
   * screen (so no signaling socket opens unless the user pairs). */
  let remoteSession: RemoteHostSession | null = null;
  startGamepadPolling(shared.input, (view) => {
    if (remoteSession) remoteSession.applyInto(view);
  });

  /* 3. Display canvas. A real, on-screen canvas (kept hidden until the first
   *    frame) whose rendering control is transferred to the GPU worker. It sits
   *    on top as a full-window overlay and ignores pointer events, so dropping
   *    another homebrew still works while a frame is shown. */
  const displayCanvas = document.createElement("canvas");
  displayCanvas.id = "voland-display";
  displayCanvas.width  = 1280;
  displayCanvas.height = 720;
  Object.assign(displayCanvas.style, {
    position: "fixed", inset: "0", width: "100vw", height: "100vh",
    display: "none", zIndex: "2147483000", background: "#000",
    objectFit: "contain", pointerEvents: "none",
  } satisfies Partial<CSSStyleDeclaration>);
  document.body.appendChild(displayCanvas);
  const offscreen = displayCanvas.transferControlToOffscreen();

  /* 4. Workers. Vite's `new URL(..., import.meta.url)` + `type: "module"`
   *    is required under COEP: require-corp. */
  const cpuWorker   = new Worker(new URL("../workers/cpu.worker.ts",   import.meta.url), { type: "module" });
  const gpuWorker   = new Worker(new URL("../workers/gpu.worker.ts",   import.meta.url), { type: "module" });
  const audioWorker = new Worker(new URL("../workers/audio.worker.ts", import.meta.url), { type: "module" });

  type Slot = "pending" | "ready" | "failed";
  let cpuSlot:   Slot = "pending";
  let gpuSlot:   Slot = "pending";
  let audioSlot: Slot = "pending";

  let cpuBackend:   string | null = null;
  let adapterLabel: string | null = null;

  /* Run-lifecycle fan-out: the CPU worker's lifecycle messages are mirrored to
   * any UI subscriber (the In-Game run-state machine) in addition to being
   * logged, so the UI reflects the live core without owning the worker. */
  const runListeners = new Set<(event: RunEvent) => void>();
  function emitRunEvent(event: RunEvent): void {
    for (const cb of runListeners) cb(event);
  }

  /* Save-data orchestration: maps a run's file name (the worker protocol's key)
   * to the content-derived titleId (the storage/manifest key), so a `saveData`
   * message that arrives when a run ends can be persisted under the right title.
   * Runs are registered when `loadGame` is intercepted below. */
  const saveOrch = new SaveOrchestrator();

  function slotGlyph(slot: Slot): string {
    return slot === "ready" ? "ok" : slot === "failed" ? "fail" : "…";
  }
  function updateStatus(): void {
    setStatus([
      `cpu=${slotGlyph(cpuSlot)}`,
      `gpu=${slotGlyph(gpuSlot)}`,
      `audio=${slotGlyph(audioSlot)}`,
    ].join(" · "));
  }
  updateStatus();

  const allReady = new Promise<void>((resolve) => {
    const maybeResolve = () => {
      if (cpuSlot !== "pending" && gpuSlot !== "pending" && audioSlot !== "pending") {
        resolve();
      }
    };

    attachWorkerLogRelay<CPUToMainMessage>("cpu", cpuWorker, msg => {
      if (msg.type === "ready") {
        cpuBackend = `${msg.backendName} v${msg.backendVersion}`;
        appendLogLine("info", `cpu backend: ${cpuBackend}`);
        cpuSlot = "ready";
        updateStatus();
        maybeResolve();
      } else if (msg.type === "halted") {
        appendLogLine("warn", "cpu halted");
        emitRunEvent({ type: "halted" });
      } else if (msg.type === "gameLoaded") {
        appendLogLine("info", `ran ${msg.name} — x0=${msg.x0}, pc=${msg.pc}`);
        emitRunEvent({ type: "loaded", name: msg.name });
      } else if (msg.type === "frame") {
        appendLogLine("info", `frame ${msg.width}x${msg.height} → display`);
        const fwd: MainToGPUMessage = {
          type: "frame", width: msg.width, height: msg.height, stride: msg.stride, pixels: msg.pixels,
        };
        gpuWorker.postMessage(fwd, [msg.pixels]);
        displayCanvas.style.display = "block";
        emitRunEvent({ type: "frame" });
      } else if (msg.type === "loadError") {
        appendLogLine("error", `load failed (${msg.name}): ${msg.message}`);
        emitRunEvent({ type: "loadError", name: msg.name, message: msg.message });
      } else if (msg.type === "saveData") {
        // The run flushed its save VFS. Map the run name back to the titleId it
        // was registered under and hand the bytes to the shell to persist (the
        // tested persistSave path). A save for a run we never tracked is dropped
        // rather than persisted under a guessed key — the drop-vs-persist
        // decision is the tested resolveSaveForPersist seam.
        const resolved = resolveSaveForPersist(saveOrch, msg.name, msg.bytes);
        if (resolved === null) {
          appendLogLine("warn", `save data for untracked run "${msg.name}" dropped`);
        } else {
          appendLogLine("info", `save data for ${msg.name} (${resolved.bytes.byteLength} bytes) → persist`);
          emitRunEvent({ type: "save", titleId: resolved.titleId, bytes: resolved.bytes });
        }
      }
    }, () => { cpuSlot = "failed"; updateStatus(); maybeResolve(); });

    attachWorkerLogRelay<GPUToMainMessage>("gpu", gpuWorker, msg => {
      if (msg.type === "ready") {
        adapterLabel = msg.adapterName ?? "Software/Unknown";
        appendLogLine("info", `gpu adapter: ${adapterLabel}`);
        gpuSlot = "ready";
        updateStatus();
        maybeResolve();
      }
    }, () => { gpuSlot = "failed"; updateStatus(); maybeResolve(); });

    attachWorkerLogRelay<AudioToMainMessage>("audio", audioWorker, msg => {
      if (msg.type === "ready") {
        appendLogLine("info", "audio worker ready");
        audioSlot = "ready";
        updateStatus();
        maybeResolve();
      }
    }, () => { audioSlot = "failed"; updateStatus(); maybeResolve(); });

    window.setTimeout(() => {
      if (cpuSlot === "pending")   { cpuSlot   = "failed"; appendLogLine("error", `cpu worker did not report within ${WORKER_READY_TIMEOUT_MS}ms`); }
      if (gpuSlot === "pending")   { gpuSlot   = "failed"; appendLogLine("error", `gpu worker did not report within ${WORKER_READY_TIMEOUT_MS}ms`); }
      if (audioSlot === "pending") { audioSlot = "failed"; appendLogLine("error", `audio worker did not report within ${WORKER_READY_TIMEOUT_MS}ms`); }
      updateStatus();
      maybeResolve();
    }, WORKER_READY_TIMEOUT_MS);
  });

  const cpuInit:   MainToCPUMessage   = { type: "init", shared };
  const gpuInit:   MainToGPUMessage   = { type: "init", canvas: offscreen, shared };
  const audioInit: MainToAudioMessage = { type: "init", audioRing: shared.audioRing };

  cpuWorker.postMessage(cpuInit);
  gpuWorker.postMessage(gpuInit, [offscreen]);
  audioWorker.postMessage(audioInit);

  appendLogLine("info", "workers posted init messages");

  /* Real save-data persistence via OPFS when the origin's private file system is
   * available, behind the same tested SaveDataStorage seam; fall back to an
   * in-memory store (process-lifetime only) where OPFS is absent. §1.6: blobs
   * are the game's own runtime save data — opaque bytes, never decrypted content.
   * Created here (before the load adapter) so a load can read the persisted save
   * to import. */
  const opfsAvailable =
    typeof navigator !== "undefined" &&
    typeof navigator.storage === "object" &&
    navigator.storage !== null &&
    typeof (navigator.storage as { getDirectory?: unknown }).getDirectory === "function";
  const saveDataStorage: SaveDataStorage = opfsAvailable
    ? createOpfsSaveDataStorage()
    : createMemorySaveDataStorage();

  /* Adapter so the read/sniff/route wiring (drop.ts) posts to the CPU worker
   * without depending on the Worker type directly. A `loadGame` is intercepted to
   * finish the save round-trip: register the run (name -> titleId) and read any
   * persisted save to import, attaching it as `loadGame.save` so the guest sees
   * its save. Other messages pass straight through. */
  const rawPostToCPU = (msg: MainToCPUMessage, transfer?: Transferable[]): void =>
    cpuWorker.postMessage(msg, transfer ?? []);
  const postToCPU: PostToCPU = (msg, transfer) => {
    if (msg.type !== "loadGame" || msg.save !== undefined) {
      rawPostToCPU(msg, transfer);
      return;
    }
    // Derive the run identity from the image we're about to load (sniff the
    // format off the same bytes the core will), register name -> titleId, then
    // read the persisted save (if any) and post loadGame with it attached. We
    // keep the `data` buffer out of the transfer list until the final post so it
    // is still readable for the sniff; the save blob joins the transfer list too.
    const data = msg.data;
    const identity: RunIdentity = {
      name: msg.name,
      size: data.byteLength,
      format: sniffFormat(new Uint8Array(data)).format,
    };
    const titleId = saveOrch.register(identity);
    void resolveImport(saveDataStorage, titleId)
      .then((save) => {
        // Shape the outgoing message + transfer list via the tested seam:
        // attach the save iff present, transferring it alongside the image.
        const { message, transfer } = prepareLoadGame(data, msg.name, save);
        if (save) appendLogLine("info", `importing save for ${msg.name} (${save.byteLength} bytes)`);
        rawPostToCPU(message, transfer);
      })
      .catch(() => {
        const { message, transfer } = prepareLoadGame(data, msg.name, null);
        rawPostToCPU(message, transfer);
      });
  };

  /* Allow dropping a homebrew NRO onto the page to load it. */
  setupGameDrop(postToCPU);
  appendLogLine("info", "drop a homebrew .nro file anywhere to load it");

  await allReady;

  /* Stand up the WebAudio output graph (worklet consumer of the SAB ring). Done
   * after the workers are up so it never blocks boot; failure here is non-fatal
   * (the app stays usable, just silent). The context starts suspended under the
   * autoplay policy and is resumed on the first user gesture below. */
  const audioOutput = await setupAudioOutput(shared.audioRing);
  if (audioOutput) {
    const resumeOnGesture = (): void => audioOutput.resume();
    for (const evt of ["pointerdown", "keydown", "touchstart"] as const) {
      window.addEventListener(evt, resumeOnGesture, { passive: true });
    }
  }

  /* Once the workers are up, honour ?load=<url> for one-click display demos. */
  autoLoadFromQuery(postToCPU);

  /* Stop a running title: tell the CPU worker to halt its frame loop and hide
   * the full-window display overlay so the UI is clean again. */
  const haltRun = (): void => {
    const halt: MainToCPUMessage = { type: "halt" };
    cpuWorker.postMessage(halt);
    displayCanvas.style.display = "none";
  };

  /* Switch the display canvas between letterbox (contain, keep aspect) and
   * stretch (fill). The canvas is created with object-fit: contain; this just
   * flips that one CSS property on the live on-screen element. */
  const setDisplayAspect = (keepAspect: boolean): void => {
    displayCanvas.style.objectFit = keepAspect ? "contain" : "fill";
  };

  /* Apply the runtime mute-audio preference: post `setMuted` to the audio worker
   * (which holds the mute flag) AND to the worklet node (the realtime consumer
   * that actually silences output). The App's settings effect drives this with the
   * persisted preference at mount and on every toggle. A presentation/runtime
   * preference only (§1.6: no game content). */
  const setAudioMuted = (muted: boolean): void => {
    const msg: MainToAudioMessage = { type: "setMuted", muted };
    audioWorker.postMessage(msg);
    audioOutput?.setMuted(muted);
  };

  /* Apply the runtime volume preference (settings.audioGain): post `setGain` to
   * the audio worker AND the worklet node (the realtime consumer that folds the
   * gain into its S16->Float32 convert). The App's settings effect drives this
   * with the persisted preference at mount and on every change. A
   * presentation/runtime preference only (§1.6: no game content). */
  const setAudioGain = (gain: number): void => {
    const msg: MainToAudioMessage = { type: "setGain", gain };
    audioWorker.postMessage(msg);
    audioOutput?.setGain(gain);
  };

  /* Real settings persistence via localStorage when available, behind the same
   * tested SettingsStorage seam; fall back to in-memory if the store is absent. */
  const settingsStorage: SettingsStorage =
    typeof localStorage !== "undefined"
      ? createLocalStorageSettings(localStorage)
      : createMemorySettingsStorage();

  /* Real manifest accounting persistence via localStorage when available, behind
   * the same tested seam; fall back to in-memory (process-lifetime only) where
   * localStorage is absent. Only small JSON (which titles have a save + sizes +
   * write times) — never blob bytes. */
  const manifestStore: ManifestStore =
    typeof localStorage !== "undefined"
      ? createLocalStorageManifest(localStorage)
      : createMemoryManifestStore();

  /* Real library persistence via localStorage when available, behind the same
   * tested LibraryStorage seam; fall back to in-memory (process-lifetime only)
   * where localStorage is absent. Only small JSON metadata (title name / sniffed
   * format / size / source) — never any game bytes (§1.6). */
  const libraryStore: LibraryStorage =
    typeof localStorage !== "undefined"
      ? createLocalStorageLibrary(localStorage)
      : createMemoryLibraryStorage();

  return {
    adapterLabel: adapterLabel ?? "unavailable",
    cpuBackend:   cpuBackend   ?? "unavailable",
    ramMiB,
    loadFile: (file: DroppableFile) => handleDroppedFile(file, postToCPU, appendLogLine),
    haltRun,
    setDisplayAspect,
    setAudioMuted,
    setAudioGain,
    settingsStorage,
    saveDataStorage,
    manifestStore,
    libraryStore,
    onRunEvent: (cb: (event: RunEvent) => void): (() => void) => {
      runListeners.add(cb);
      return () => { runListeners.delete(cb); };
    },
    /* Track C: the Remote screen starts a pairing session on demand; the poll
     * hook above merges its input. Restarting closes any prior session. */
    startRemoteControl: (): RemoteHostSession => {
      remoteSession?.close();
      remoteSession = startRemoteHost();
      return remoteSession;
    },
    stopRemoteControl: (): void => {
      remoteSession?.close();
      remoteSession = null;
    },
  };
}

/* Register the (network-only, COEP-preserving) service worker so the app is
 * installable as a PWA on iOS / Android / desktop. Best-effort and off the boot
 * critical path: a failure here never blocks the emulator. See public/sw.js. */
function registerServiceWorker(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  if (location.protocol !== "https:" && location.hostname !== "localhost") return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => { /* non-fatal */ });
  });
}

registerServiceWorker();

boot()
  .then(async result => {
    if (!result) return;
    appendLogLine("info", "core online - mounting Solid shell");
    const { mountShell } = await import("./ui/mount");
    mountShell(result);
  })
  .catch(e => fatal(`unhandled boot error: ${(e as Error).message}`));
